import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import TableCell from '@material-ui/core/TableCell'
import { makeStyles } from '@material-ui/core/styles'
import SessionDetailsDialog from 'components/session-details-dialog'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import { useAuth } from 'lib/auth'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import { RefetchContext } from 'lib/utils/refetchContext'
import { onCancellation, onPayment, onReschedule } from 'lib/utils'
import { withEnrollmentCredit } from 'lib/sessions/utils'
import { parseEvent } from 'utils/eventUtils'
import { useGetSessions } from 'pages/pga-coach/students/student-show-page/api-hooks'
import { useStudentListNameAgeStyles } from 'pages/pga-coach/students/student-list-name-age-styles'
import { formatShortDate } from './format'
import EmptyValue from './empty-value'

const useStyles = makeStyles(theme => ({
  // Plain table text that happens to be clickable.
  trigger: {
    display: 'block',
    maxWidth: '100%',
    padding: 0,
    border: 0,
    background: 'none',
    color: 'inherit',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left',
    outline: 'none',
  },
  // Only for Tab navigation; focus handed back when the dialog closes
  // must not draw anything.
  keyboardFocus: {
    textDecoration: 'underline',
  },
  backdrop: {
    zIndex: theme.zIndex.modal,
    color: theme.palette.common.white,
  },
}))

const sameInstant = (a, b) =>
  !!a && !!b && new Date(a).getTime() === new Date(b).getTime()

// The campaign only records when the recipient last booked (`lastBookedAt`,
// the start time of that lesson), so the lesson itself is found among the
// student's enrollments.
export const findLastLessonEnrollment = (enrollments, lastBookedAt) => {
  const matches = (enrollments || []).filter(({ lesson }) =>
    sameInstant(lesson?.startDateTime, lastBookedAt),
  )
  return (
    matches.find(
      ({ isCanceled, lesson }) => !isCanceled && !lesson.isCanceled,
    ) || matches[0]
  )
}

const LastLessonDialog = ({ studentId, lastBookedAt, onClose }) => {
  const classes = useStyles()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const { data, loading, error, refetch } = useGetSessions(
    studentId,
    user.coach.id,
  )

  const loadingFirstResult = loading && !data
  const enrollment = findLastLessonEnrollment(
    data?.studentEnrollments,
    lastBookedAt,
  )
  const lesson = enrollment?.lesson
  const notFound = !loadingFirstResult && !lesson

  useEffect(() => {
    if (notFound) {
      snackbarError(
        error
          ? 'Could not load this lesson. Try again.'
          : 'We could not find the details for this lesson.',
      )
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound])

  const closeWhenDismissed = value => {
    if (!value) onClose()
  }

  if (loadingFirstResult) {
    return (
      <Backdrop open className={classes.backdrop} aria-label="Loading lesson">
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }
  if (!lesson) return null

  const refresh = () => refetch()
  return (
    <RefetchContext.Provider value={refresh}>
      {lesson.__typename === 'GroupEvent' ? (
        <EventDetailsDialog
          isOpen
          setIsOpen={closeWhenDismissed}
          event={parseEvent(lesson)}
          onPayment={() => onPayment(refresh)}
        />
      ) : (
        <SessionDetailsDialog
          isOpen
          setIsOpen={closeWhenDismissed}
          booking={withEnrollmentCredit(lesson, enrollment)}
          onReschedule={() => onReschedule(refresh)}
          onPayment={() => onPayment(refresh)}
          onCancellation={() => onCancellation(refresh)}
        />
      )}
    </RefetchContext.Provider>
  )
}

LastLessonDialog.propTypes = {
  studentId: PropTypes.string.isRequired,
  lastBookedAt: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

// Last lesson before the campaign: its date, opening that lesson's details.
// Without a last lesson it shows a dash.
export const LastLessonContent = ({ recipient }) => {
  const classes = useStyles()
  const nameAgeClasses = useStudentListNameAgeStyles()
  const [open, setOpen] = useState(false)
  const [tabbedTo, setTabbedTo] = useState(false)
  const { lastBookedAt, student } = recipient
  const date = formatShortDate(lastBookedAt)

  if (!lastBookedAt) return <EmptyValue label="No previous lesson" />
  if (!student?.id) return <span className={nameAgeClasses.text}>{date}</span>

  return (
    <>
      <button
        type="button"
        className={`${nameAgeClasses.text} ${classes.trigger} ${
          tabbedTo ? classes.keyboardFocus : ''
        }`}
        aria-label={`View last lesson, ${date}`}
        onClick={() => setOpen(true)}
        onKeyUp={event => event.key === 'Tab' && setTabbedTo(true)}
        onBlur={() => setTabbedTo(false)}
      >
        {date}
      </button>
      {open ? (
        <LastLessonDialog
          studentId={student.id}
          lastBookedAt={lastBookedAt}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}

const recipientPropType = PropTypes.shape({
  lastBookedAt: PropTypes.string,
  student: PropTypes.shape({
    id: PropTypes.string,
  }),
}).isRequired

LastLessonContent.propTypes = { recipient: recipientPropType }

const LastLessonCell = ({ recipient }) => (
  <TableCell>
    <LastLessonContent recipient={recipient} />
  </TableCell>
)

LastLessonCell.propTypes = { recipient: recipientPropType }

export default LastLessonCell
